import React, { useMemo, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Helmet from 'react-helmet';
import ViewContainer from 'components/utils/ViewContainer';
import { Card, CardBody, CardHeader, Col, Row, Input } from 'reactstrap';
import InteractiveRemoteTable from 'components/utils/InteractiveRemoteTable';
import { withApiData, ApiDataBinding } from 'react-api-data';
import { ApiResponse, CrossIxConnection } from 'constants/api';
import { compose } from 'recompose';
import { get, debounce } from 'lodash';
import { NetworkRequestStatus } from 'constants/literals';
import CrossIxConnectionAddModal from 'components/modals/CrossIxConnectionAddModal';
import SyncButton from 'components/utils/SyncButton';
import { Link } from 'react-router-dom';
import { getLinkToCrossIxConnection } from 'utils/linksUtils';
import CrossIxConnectionDeleteModal from 'components/modals/CrossIxConnectionDeleteModal';

const SIZE_PER_PAGE = 30;
const DEFAULT_SORT_FIELD: keyof CrossIxConnection = 'source_exchange_name';
const DEFAULT_SORT_ORDER = 'asc';

interface FieldColumn {
    dataField: keyof CrossIxConnection;
    text: string;
    sort?: boolean;
}

interface DummyColumn {
    dataField: string;
    isDummyField: true;
    text: string;
    formatter: (cell: any, row: CrossIxConnection) => ReactNode;
}

type TableColumnDefinition = FieldColumn | DummyColumn;

interface ConnectProps {
    crossIxConnectionsBinding: ApiDataBinding<ApiResponse<{ cross_ix_connections: CrossIxConnection[] }>>;
}

const enhance = compose<ConnectProps, {}>(
    withApiData({ crossIxConnectionsBinding: 'getCrossIxConnections' }, () => ({
        crossIxConnectionsBinding: {
            page: 1,
            per_page: SIZE_PER_PAGE,
            sort: DEFAULT_SORT_FIELD,
            order: DEFAULT_SORT_ORDER,
        },
    }))
);

function CrossIxConnectionsView(props: ConnectProps) {
    const { crossIxConnectionsBinding } = props;
    const { t } = useTranslation();
    const fetchConnections = useCallback(
        (params?: object) => {
            crossIxConnectionsBinding.perform({ ...crossIxConnectionsBinding.request.params, ...params });
        },
        [crossIxConnectionsBinding]
    );
    const tableColumns = useMemo(
        (): TableColumnDefinition[] => [
            {
                dataField: 'xml_id',
                text: t('crossIxConnection.xmlId'),
                sort: true,
            },
            {
                dataField: 'source_exchange_name',
                text: t('crossIxConnection.sourceExchangeName'),
                sort: true,
            },
            {
                dataField: 'destination_exchange_name',
                text: t('crossIxConnection.destinationExchangeName'),
                sort: true,
            },
            {
                dataField: 'target_vlan_name',
                text: t('crossIxConnection.targetVlanName'),
                sort: true,
            },
            {
                dataField: 'source_exchange_customer_link_vlan_xml_id',
                text: t('crossIxConnection.sourceExchangeCustomerLinkVlanXmlId'),
                sort: true,
            },
            {
                dataField: 'destination_exchange_customer_link_vlan_xml_id',
                text: t('crossIxConnection.destinationExchangeCustomerLinkVlanXmlId'),
                sort: true,
            },
            {
                dataField: 'link',
                isDummyField: true,
                text: t('common.link'),
                formatter: (_cell: any, row: CrossIxConnection) => (
                    <Link to={getLinkToCrossIxConnection(row.uuid)}>{t('common.view')}</Link>
                ),
            },
            {
                dataField: 'delete',
                isDummyField: true,
                text: t('common.delete'),
                formatter: (_cell: any, row: CrossIxConnection) => (
                    <CrossIxConnectionDeleteModal
                        uuid={row.uuid}
                        xmlId={row.xml_id}
                        onSuccess={() => fetchConnections()}
                    />
                ),
            },
        ],
        [t, fetchConnections]
    );

    function filterConnections(searchTerm: string) {
        fetchConnections({ page: 1, search_for: searchTerm });
    }
    const debouncedFilterConnections = debounce(filterConnections, 500);

    return (
        <>
            <Helmet title={t('crossIxConnections.pageTitle')} />
            <ViewContainer>
                <Row>
                    <Col>
                        <div className="d-flex justify-content-between">
                            <div className="d-flex align-items-center">
                                <h2>{t('crossIxConnections.pageTitle')}</h2>
                                <SyncButton
                                    data-test="cross-ix-connections-refresh-btn"
                                    className="ml-2"
                                    title={t('crossIxConnections.refreshButton')}
                                    onClick={fetchConnections}
                                />
                            </div>
                            <CrossIxConnectionAddModal />
                        </div>
                        <hr />
                        <Card className="card-accent-primary">
                            <CardHeader>
                                <Row className="justify-content-between">
                                    <Col xl={3}>
                                        <Input
                                            data-test="search-term-control"
                                            defaultValue={''}
                                            onChange={(e) => debouncedFilterConnections(e.target.value)}
                                            className="form-control"
                                            placeholder={t('table.searchInputPlaceholder')}
                                        />
                                    </Col>
                                </Row>
                            </CardHeader>
                            <CardBody>
                                <InteractiveRemoteTable
                                    keyField="uuid"
                                    columns={tableColumns}
                                    data={get(crossIxConnectionsBinding, 'data.data.cross_ix_connections', [])}
                                    paginationOptions={{
                                        page: get(crossIxConnectionsBinding, 'data.meta.page', 1),
                                        sizePerPage: get(
                                            crossIxConnectionsBinding,
                                            'data.meta.page_size',
                                            SIZE_PER_PAGE
                                        ),
                                        totalSize: get(crossIxConnectionsBinding, 'data.meta.total_items', 0),
                                    }}
                                    defaultSorted={{ dataField: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_ORDER }}
                                    loading={
                                        crossIxConnectionsBinding.request.networkStatus === NetworkRequestStatus.Loading
                                    }
                                    fetchPage={(pageNumber, pageSize, sortField, sortOrder) =>
                                        fetchConnections({
                                            page: pageNumber,
                                            per_page: pageSize,
                                            sort: sortField,
                                            order: sortOrder,
                                        })
                                    }
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </ViewContainer>
        </>
    );
}

export default enhance(CrossIxConnectionsView);
